import type { SpecificationValue } from "@shared/types";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { useState } from "react";
import type { SpecTemplateAttribute } from "@/features/catalog/services/catalog-api.service";

/**
 * One input, rendered from one spec-template attribute definition.
 *
 * This component is why plan 008 calls the Specifications tab the highest-value
 * screen in the plan: the catalogue has ~674 leaf categories, each with its own
 * attributes, and a per-category form would mean a code change for every new
 * category anyone adds. Everything here comes from the DATABASE -- the label,
 * the control, the options, whether it is required.
 *
 * NOTHING IN THIS FILE MAY NAME A CATALOGUE ATTRIBUTE -- not a tile's abrasion
 * rating, not a paint's coverage, not any of the others. Plan 008's V2 greps
 * `apps/admin/src` for three of those names, because a single hard-coded one is
 * the difference between a form the taxonomy drives and a form that needs a
 * developer.
 *
 * The names themselves are deliberately not written out even here: a check that
 * greps for a string cannot tell a rule from a violation, and a comment stating
 * the rule should not be what fails it.
 */

/*
 * The shared type, re-exported rather than re-declared. This file had its own
 * `string | number | boolean` sitting beside `SpecificationValue` in
 * `@shared/types` -- a third copy of a union, in the same change that moved two
 * other shapes into shared-types precisely because copies drift.
 */
export type SpecValue = SpecificationValue;

/*
 * The "no answer" option's value.
 *
 * A sentinel rather than `""`, because `@radix-ui/react-select` throws on a
 * `SelectItem` with an empty value -- it reserves the empty string for "nothing
 * selected". Without an option like this, an OPTIONAL enum or boolean could be
 * answered but never un-answered: every value in the list is an answer, and the
 * only way back to absent was to reload the page and not touch the field.
 *
 * It is prefixed to stay out of any real template's value space; a value
 * genuinely equal to it would render as the clear option, which is a trade this
 * file cannot avoid while Radix owns the empty string.
 */
const UNANSWERED = "__finishd_unanswered__";

/** Whether a value counts as supplied, for required-field purposes. */
export function isFilled(value: SpecValue | undefined): boolean {
	if (value === undefined || value === null) return false;
	// A blank string is the state an operator reaches by tabbing through, and
	// treating it as filled is how a required attribute ends up empty. `false`
	// and `0`, by contrast, are answers.
	if (typeof value === "string") return value.trim() !== "";
	return true;
}

export function SpecAttributeField({
	attribute,
	value,
	onChange,
	invalid,
	disabled,
}: {
	attribute: SpecTemplateAttribute;
	value: SpecValue | undefined;
	onChange: (value: SpecValue | undefined) => void;
	/** The publish gate named this attribute as missing. */
	invalid?: boolean;
	disabled?: boolean;
}) {
	const id = `spec-${attribute.id}`;
	const describedBy = invalid ? `${id}-error` : undefined;

	return (
		<div className="grid gap-1.5">
			<Label htmlFor={id} className="flex items-center gap-1.5">
				{attribute.name}
				{attribute.isRequired ? (
					<>
						{/*
						 * The asterisk is decoration and the word is the message. An
						 * `aria-label` on a bare span does nothing -- a span has no role to
						 * carry it, which is what biome flags -- so the visible mark is
						 * hidden from assistive tech and real text is provided beside it.
						 */}
						<span className="text-red-600" aria-hidden="true">
							*
						</span>
						<span className="sr-only">required</span>
					</>
				) : null}
				{attribute.unit ? (
					<span className="text-xs font-normal text-muted-foreground">
						({attribute.unit})
					</span>
				) : null}
			</Label>

			{renderControl()}

			{invalid ? (
				<p id={`${id}-error`} className="text-xs text-red-600">
					Required before this product can be published.
				</p>
			) : null}
		</div>
	);

	function renderControl() {
		switch (attribute.attributeType) {
			case "boolean":
				/*
				 * Yes/No rather than a Switch, because a switch has two states and this
				 * value has three: true, false, and never answered.
				 *
				 * A Switch renders `false` and `absent` identically and can never
				 * produce `undefined`, so a REQUIRED boolean was a dead end -- the
				 * publish gate counts absent as a blocker, and an operator who wanted
				 * to answer "no" had no way to make the gate see an answer. Toggling on
				 * and off again just returned to a state the gate still called empty.
				 */
				return (
					<Select
						value={value === undefined || value === null ? "" : String(value)}
						disabled={disabled}
						onValueChange={(next) =>
							onChange(next === UNANSWERED ? undefined : next === "true")
						}
					>
						<SelectTrigger
							id={id}
							aria-describedby={describedBy}
							aria-invalid={invalid || undefined}
							aria-required={attribute.isRequired || undefined}
						>
							<SelectValue placeholder="Not answered" />
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								<SelectItem value="true">Yes</SelectItem>
								<SelectItem value="false">No</SelectItem>
								{attribute.isRequired ? null : (
									<SelectItem value={UNANSWERED}>Not answered</SelectItem>
								)}
							</SelectGroup>
						</SelectContent>
					</Select>
				);

			case "number":
				return (
					<NumberSpecInput
						id={id}
						value={value}
						disabled={disabled}
						describedBy={describedBy}
						invalid={invalid}
						required={attribute.isRequired}
						onChange={onChange}
					/>
				);

			case "enum": {
				/*
				 * Blank options are FILTERED, and that is not tidiness.
				 * `@radix-ui/react-select` throws on `value=""`, and the app's only
				 * error boundary wraps the router -- so one empty string in a template's
				 * enumValues took the entire admin app down rather than degrading this
				 * one field.
				 */
				const options = (attribute.enumValues ?? []).filter(
					(option) => option.trim() !== "",
				);
				// An enum template with no usable values is a template bug, not an
				// operator problem -- fall back to free text so the product is still
				// editable rather than presenting an empty dropdown.
				if (options.length === 0) return renderText();

				/*
				 * A stored value the template no longer offers is shown as an option
				 * rather than dropped. Radix renders an unmatched value as the
				 * placeholder, so the field looked empty while `specifications` still
				 * held the old value and resent it on save -- a value the operator
				 * could neither see nor clear.
				 */
				/*
				 * STRINGIFIED, not string-only.
				 *
				 * `typeof value === "string" ? value : ""` sent a stored number or
				 * boolean to the placeholder, so an attribute whose `attributeType`
				 * changed from number to enum after products were saved showed an empty
				 * field while `specifications` still held the value and resent it on
				 * every save. A template with `enumValues: ["30", "60"]` and a stored
				 * `60` is the ordinary version of this, not a corner case.
				 */
				const storedValue =
					value === undefined || value === null ? "" : String(value);
				const isOrphan = storedValue !== "" && !options.includes(storedValue);
				return (
					<Select
						value={storedValue}
						disabled={disabled}
						onValueChange={(next) =>
							onChange(next === UNANSWERED ? undefined : next)
						}
					>
						{/*
						 * No `aria-label` here: the field already has a real <Label
						 * htmlFor>, and adding one both duplicates the name and overrides
						 * the association. The product list's Selects DO carry one, because
						 * they have no label of their own.
						 */}
						<SelectTrigger
							id={id}
							aria-describedby={describedBy}
							aria-invalid={invalid || undefined}
							aria-required={attribute.isRequired || undefined}
						>
							<SelectValue placeholder={`Select ${attribute.name}`} />
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								{isOrphan ? (
									<SelectItem value={storedValue}>
										{storedValue} (no longer offered)
									</SelectItem>
								) : null}
								{/*
								 * De-duplicated. A hand-seeded `enumValues` with the same
								 * option twice produced two SelectItems with the same React
								 * key, and Radix keys its own value registry the same way.
								 */}
								{Array.from(new Set(options)).map((option) => (
									<SelectItem key={option} value={option}>
										{option}
									</SelectItem>
								))}
								{attribute.isRequired ? null : (
									<SelectItem value={UNANSWERED}>Not answered</SelectItem>
								)}
							</SelectGroup>
						</SelectContent>
					</Select>
				);
			}

			default:
				return renderText();
		}
	}

	function renderText() {
		return (
			<Input
				id={id}
				disabled={disabled}
				aria-describedby={describedBy}
				aria-invalid={invalid || undefined}
				aria-required={attribute.isRequired || undefined}
				value={typeof value === "boolean" ? String(value) : (value ?? "")}
				onChange={(event) =>
					onChange(event.target.value === "" ? undefined : event.target.value)
				}
			/>
		);
	}
}

/**
 * A numeric specification, holding the operator's RAW TEXT while they type.
 *
 * A controlled `<input type="number">` cannot do this. Its `value` returns ""
 * for anything that is not yet a valid number -- "8." on the way to "8.5", or a
 * lone "-" on the way to "-3" -- so a controlled component parses "" to
 * `undefined`, re-renders empty, and deletes the digits already typed. The
 * decimal is not reachable at all.
 *
 * So the text lives here and only completed numbers go out. `type="text"` with
 * `inputMode="decimal"` keeps the numeric keypad on a phone without handing the
 * browser's own sanitiser a value it will edit mid-keystroke.
 */
function NumberSpecInput({
	id,
	value,
	disabled,
	describedBy,
	invalid,
	required,
	onChange,
}: {
	id: string;
	value: SpecValue | undefined;
	disabled?: boolean;
	describedBy?: string;
	invalid?: boolean;
	required?: boolean;
	onChange: (value: SpecValue | undefined) => void;
}) {
	const external = value === undefined || value === null ? "" : String(value);
	const [text, setText] = useState(external);
	const [lastExternal, setLastExternal] = useState(external);

	/*
	 * Follow the value when it changes from OUTSIDE -- a reseed after save, or
	 * navigating to another product -- without fighting the operator's keystrokes.
	 *
	 * Adjusted during render rather than in an effect: React re-runs this
	 * component before committing, so there is no flash of the stale text and no
	 * dependency list to get wrong. The numeric comparison is what keeps the
	 * component's own round trip quiet -- typing "8." emits 8, which arrives back
	 * as "8", and a string comparison would overwrite the decimal point the
	 * operator had just typed.
	 */
	if (external !== lastExternal) {
		setLastExternal(external);
		if (
			Number(text) !== Number(external) ||
			(text === "") !== (external === "")
		) {
			setText(external);
		}
	}

	return (
		<Input
			id={id}
			type="text"
			inputMode="decimal"
			disabled={disabled}
			aria-describedby={describedBy}
			aria-invalid={invalid || undefined}
			aria-required={required || undefined}
			value={text}
			onChange={(event) => {
				const raw = event.target.value;
				setText(raw);

				// Clearing the field means "no value", not 0 -- zero is an answer and
				// absence is not, and the publish gate distinguishes them.
				if (raw.trim() === "") return onChange(undefined);

				// A NUMBER attribute never emits a string. The backend type-checks
				// specifications and rejects the whole save with `Specification "X"
				// must be a number`, naming a field the operator may not even be
				// looking at. Intermediate text stays local until it parses.
				const parsed = Number(raw);
				if (Number.isNaN(parsed)) return;
				onChange(parsed);
			}}
		/>
	);
}
